import './Dialog.scss';
import NP, {EventArgs, EventKeyArgs} from "../../NP";
import Control from "../../Control";
import Delegate from "../../Delegate";
import TopAppBar from "../TopAppBar/TopAppBar";
import Button from "../Button/Button";
import IconToggleButton from "../IconToggleButton/IconToggleButton";

const DIALOG_CONTENT_WIDTH = 420;
const DIALOG_MESSAGE_WIDTH = 380;

export interface DialogCloseEventArgs {
	IsCancelClose: boolean;
}

export default class Dialog extends Control {

	EvOpened: Delegate;
	EvOpenEnd: Delegate;
	EvClosed: Delegate;
	EvOkButtonClick: Delegate;
	EvCancelButtonClick: Delegate;
	EvBackButtonClick: Delegate;
	EvSearch: Delegate;
	EvKeyDown: Delegate;

	_CustomTopAppBar: TopAppBar;
	_ShowHeader: boolean;
	_ShowSearch: boolean;
	_Selectable: boolean;
	_AutoDestroy: boolean;
	_ShowCloseButton: boolean;
	_ShowBackButton: boolean;
	_IsOuterCloseButton: boolean;
	_Caption: string;
	_IsAdaptive: boolean;
	_ShowButtons: boolean;
	_CloseOnFrameMouseDown: boolean;
	_ContentAutoFit: boolean;
	_Mode: string;
	_OkRK: string;
	_CancelRK: string;
	_FlexContent: boolean;
	_DialogActionsCustomNode: boolean;
	_DialogActionsNoShadow: boolean;
	_IsMessageDialog: boolean;
	_AutoFocusInput: boolean;

	_contentDomNode: HTMLElement;
	_contentNode: HTMLElement;
	_buttonsNode: HTMLElement;
	_backdrop: HTMLElement;
	_container: HTMLElement;
	_customButtonNode: HTMLElement;
	_outerButtonsNode: HTMLElement;

	_topAppBar: TopAppBar;  //temp
	_cancelButton: Button;
	_closeButton: IconToggleButton;
	_okButton: Button;
	_onResizeTimeout: any;

	_wndResizeBind: EventListenerOrEventListenerObject;

	_DroppedDown: boolean;
	protected _UseSinglePaddingContent: boolean;

	static _detachedFrame: HTMLElement;
	static _keepDetachedFrame: boolean;

	constructor(settings?: object)
	{
		super(settings);
		this._render();
	}

	_initFields(settings: object)
	{
		this._CustomTopAppBar = null;
		this._ShowHeader =
			 this._ShowSearch =
				  this._Selectable =
						this._AutoDestroy =
							 this._DialogActionsCustomNode =
								  this._DialogActionsNoShadow =
										this._IsMessageDialog =
												  this._ShowBackButton =
														this._IsOuterCloseButton =
															 this._ShowCloseButton = false;
		this._AutoFocusInput = true;
		this._Caption = '';
		this._IsAdaptive =
			 this._ShowButtons = true;
		this._CloseOnFrameMouseDown = false;
		this._ContentAutoFit = false;
		this._Mode = null;
		this._OkRK = 'Ok';
		this._CancelRK = 'Cancel';
		this._FlexContent = false;

		this._UseSinglePaddingContent = true;
		this.EvOpened = new Delegate();
		this.EvClosed = new Delegate();
		this.EvOpenEnd = new Delegate();
		this.EvOkButtonClick = new Delegate();
		this.EvCancelButtonClick = new Delegate();
		this.EvBackButtonClick = new Delegate();
		this.EvSearch = new Delegate();
		this.EvKeyDown = new Delegate();

		super._initFields(settings);
		if (!this._Width || this._Width == 0)
			this._Width = !this._IsMessageDialog ? DIALOG_CONTENT_WIDTH : DIALOG_MESSAGE_WIDTH;

		if (!this._ParentNode)
			this._ParentNode = document.body;
	}

	_render()
	{
		if (this._Mode === 'FullScreen')
			this._Width = null;

		this.np_render(`<div class="dialog">
  <div class="dialog__container">
    <div class="dialog__surface" tabindex="1">
      <div class="dialog__content  ${this._Selectable ? ' selectable' : ''}"></div>
      <footer class="dialog__actions"></footer>
    </div>
  </div>
  <div class="dialog__scrim"></div>
</div>`, false);

		this.addClass('dialog--no-animation');

		this._contentDomNode = this._root.querySelector('.dialog__surface');
		this._contentNode = this._root.querySelector('.dialog__content');
		this._buttonsNode = this._root.querySelector('.dialog__actions');
		this._backdrop = this._root.querySelector('.dialog__scrim');
		this._container = this._root.querySelector('.dialog__container');
		this.addEvent(this._backdrop, 'click', this._onBackdropClick);

		if (!this._UseSinglePaddingContent)
			NP.addCssClass(this._contentNode, 'nopad-content');

		this._topAppBar = this._CustomTopAppBar || new TopAppBar({
			ShowSearchButton: this._ShowSearch,
			ShowBackButton: this._ShowBackButton,
			Class: 'top-app-bar--white',
			EvSearch: (s: object, e: any) => this._onSearch(e.Value),
			EvHideSearch: () => this._onSearch(''),
			EvBackClick: () => this._onBackButtonClick(),
			MainButton: {
				FontImage: NP.FontImages.close,
				EvClick: () => this._onCancelButtonClick()
			}
		});

		this._topAppBar.putToNode(this._contentNode.parentNode as HTMLElement, this._contentNode);

		const isFullscreen = this._Mode === 'FullScreen';
		if (isFullscreen)
			NP.addCssClass(this._contentDomNode, 'dialog__surface--fullscreen');

		if (this._Mode == 'FullHeight')
			NP.addCssClass(this._container, 'dialog__container--full-height');

		if (this._ShowCloseButton || isFullscreen)
			this._closeButton = this._topAppBar.getMainButton() as IconToggleButton;

		if (this._IsOuterCloseButton && !isFullscreen)
			this.setCloseButtonIsOuter(true);

		if (!isFullscreen)
		{
			this._cancelButton = new Button({
				Class: 'dialog__button',
				ParentNode: this._buttonsNode,
				RK: this._CancelRK,
				EvClick: () => this._onCancelButtonClick()
			});
		}

		this._okButton = new Button({
			Class: 'dialog__button dialog__action',
			ParentNode: this._buttonsNode,
			RK: this._OkRK,
			EvClick: () => this._onOkButtonClick()
		});

		if (isFullscreen)
			this._topAppBar.addItem(this._okButton);

		if (this._FlexContent)
			NP.addCssClass(this._contentNode, 'dialog__content--flex');

		if (this._IsMessageDialog)
		{
			this._DialogActionsCustomNode = true;
			this._DialogActionsNoShadow = true;
			NP.addCssClass(this._contentNode, 'dialog__content--message');
			this.getTopAppBar().addClass('top-app-bar--no-shadow');
		}

		if (this._DialogActionsCustomNode)
		{
			NP.removeCssClass(this._buttonsNode, 'dialog__actions');
			NP.addCssClass(this._buttonsNode, 'dialog__footer-node');
			this._okButton.addClass('app-button--unelevated');
			this._cancelButton.addClass('app-button--unelevated app-button--neutral');
		}

		if (this._DialogActionsNoShadow)
		{
			if (this._DialogActionsCustomNode)
				NP.addCssClass(this._buttonsNode, 'dialog__footer-node--no-shadow');
			else
				NP.addCssClass(this._buttonsNode, 'dialog__actions--no-shadow');
		}

		if (NP.isNumber(this._Width) && this._Width > 0)
			this._contentDomNode.style.width = this._Width + 'px';

		if (NP.isNumber(this._Height) && this._Height > 0)
		{
			this._contentDomNode.style.height = this._Height + 'px';
			this._container.style.height = this._Height + 'px';
		}

		this.setShowHeader(this._ShowHeader);
		this.setCaption(this._Caption);
		const content = this._Content;
		this._Content = null;
		if (!this._Content && content)
			this.setContent(content);
		this.setShowButtons(this._ShowButtons);

		this.setRK(this._RK, true);

		this.addEvent(this._contentNode, 'scroll', this._onContentScroll);
	}

	setCustomAppBar(appBar: TopAppBar)
	{
		this._CustomTopAppBar = appBar;
		this._topAppBar.destroy();
		this._topAppBar = this._CustomTopAppBar;
		this._topAppBar.putToNode(this._contentNode.parentNode as HTMLElement, this._contentNode);

		if (this._outerButtonsNode)
		{
			this._container.removeChild(this._outerButtonsNode);
			this._outerButtonsNode = null;
		}

		const isFullscreen = this._Mode === 'FullScreen';
		if (this._ShowCloseButton || isFullscreen)
		{
			this._closeButton = this._topAppBar.getMainButton() as IconToggleButton;
			this._closeButton.setFontImage(NP.FontImages.close);
			this._closeButton.setToolTip(NP.RM.getString("Close"));
			this._closeButton.EvClick.add(this._onCancelButtonClick, this);
			this._closeButton.show();
		}

		if (this._IsOuterCloseButton && !isFullscreen)
			this.setCloseButtonIsOuter(true);

		if (this._ShowBackButton)
		{
			this.getTopAppBar().setShowBackButton(true)
			this.getTopAppBar()._backBtn.EvClick.add(this._onBackButtonClick, this);
		}
	}

	_onContentScroll()
	{
		this._topAppBar.setScrollShadowVisible(this._contentNode.scrollTop > 0);
	}

	_renderItems(items: Array<HTMLElement | Control | string>)
	{
		super._renderItems(items, this._contentNode);
	}

	_onKeyDown(sender: object, args: EventKeyArgs)
	{
		if (args.Event.keyCode == NP.Key.Esc)
		{
			NP.DropPanel.closeNonDialogPanels();
			this._onCancelButtonClick();
			args.Event.preventDefault();
			args.Event.stopPropagation();
		}
	}

	_onSearch(text: string)
	{
		this.EvSearch.fire(this, {Value: text});
	}

	_bindEvents()
	{
		if (!this._isBind)
		{
			super._bindEvents();
			window.addEventListener('resize', this._wndResizeBind = this._onWndResize.bind(this), false);
		}
	}

	_unBindEvents()
	{
		window.removeEventListener('resize', this._wndResizeBind);
		super._unBindEvents();
	}

	_onBackdropClick()
	{
		if (this._CloseOnFrameMouseDown && !NP.hasCssClass(this._root, ('dialog--opening')))
			this.hide(true);
	}

	_onWndResize()
	{
		if (this._onResizeTimeout)
			clearTimeout(this._onResizeTimeout);
		this._onResizeTimeout = setTimeout(() => this._checkIsAdaptive(), 50);
	}

	_checkIsAdaptive()
	{
		let docWidth = document.documentElement.clientWidth;
		if (this._IsAdaptive)
		{
			if (docWidth < 600)
			{
				NP.addCssClass(this._contentDomNode, 'dialog__surface--fullscreen');
				NP.addCssClass(this._container, 'dialog__container--fullscreen');
			}
			else if (this._Mode !== 'FullScreen')
			{
				NP.removeCssClass(this._contentDomNode, 'dialog__surface--fullscreen');
				NP.removeCssClass(this._container, 'dialog__container--fullscreen');
			}
		}

		if (this._Mode === 'FullScreen')
		{
			this.setWidth(document.documentElement.clientWidth);
			this.setHeight(document.documentElement.clientHeight);
		}

		if (this._ContentAutoFit && this._Content instanceof Control)
		{
			let ctrl = this._Content as Control;
			let w = this._IsAdaptive && docWidth < 600 ? this._contentNode.offsetWidth : this._Width;
			if (!this._Class || this._Class.indexOf('nopad') == -1)
				w -= 48;
			ctrl.setWidth(w);
			let h = 0;
			if (this._ShowHeader)
				h += 64;
			if (this._ShowButtons)
				h += 64;
			if (!this._Height)
				ctrl.setHeight(this._container.offsetHeight - h);
			else
				ctrl.setHeight(this._Height - h);
		}
	}

	getTopAppBar()
	{
		return this._topAppBar;
	}

	getCloseButton()
	{
		return this._closeButton;
	}

	_onCancelButtonClick()
	{
		let e = {IsCancelClose: false} as DialogCloseEventArgs;
		this.EvCancelButtonClick.fire(this, e);
		!e.IsCancelClose && this.hide();
	}

	_onBackButtonClick()
	{
		let e = {IsCancelClose: false} as DialogCloseEventArgs;
		this.EvBackButtonClick.fire(this, e);
		!e.IsCancelClose && this.hide();
	}

	_onOkButtonClick()
	{
		const a = {IsCancelClose: false} as DialogCloseEventArgs;
		this.EvOkButtonClick.fire(this, a);
		!a.IsCancelClose && this.hide();
	}

	protected onOpenEnd()
	{
		this.EvOpenEnd.fire(this);
	}

	getCancelButton()
	{
		return this._cancelButton;
	}

	getOkButton()
	{
		return this._okButton;
	}

	setShowHeader(value: boolean)
	{
		this._ShowHeader = !!value;
		this._topAppBar.setIsVisible(this._ShowHeader);
	}

	setTopAppBar(value: TopAppBar)
	{
		if (this._topAppBar)
			this._topAppBar.hide();
		this._topAppBar = value;
		if (this._topAppBar)
		{
			this._topAppBar.putToNode(this._contentNode.parentNode as HTMLElement, this._contentNode);
			this.setShowHeader(true);
		}
	}

	setButtonsNode(value: HTMLElement)
	{
		NP.removeCssClass(this._customButtonNode, 'dialog__actions');
		NP.removeFromParent(this._customButtonNode);
		if (value)
		{
			this._customButtonNode = value;
			NP.addCssClass(value, 'dialog__actions');
			this._contentDomNode.appendChild(value);
		}
	}

	setShowButtons(value: boolean)
	{
		this._ShowButtons = !!value;
		if (this._ShowButtons)
		{
			if (!this._buttonsNode.parentNode)
				this._contentDomNode.appendChild(this._buttonsNode);
		}
		else
		{
			NP.removeFromParent(this._buttonsNode);
		}
	}

	getIsVisible()
	{
		return this.hasClass('dialog--open');
	}

	setCaption(value: string)
	{
		this._Caption = value;
		this._topAppBar.setCaption(this._Caption);
	}

	setContent(value: object)
	{
		this._setContentToNode(value, this._contentNode, "Content", true);
	}

	getButtonsNode()
	{
		return this._buttonsNode;
	}

	show()
	{
		NP.getOpenedDialogs().push(this);

		this._DroppedDown = true;
		if (this._Mode == 'FullScreen')
			this._root.style.visibility = 'hidden';

		super.show();
		this.addClass('dialog--open');
		this.EvOpened.fire(this);
		this.onOpenEnd();

		if (this._Mode == 'FullScreen')
			this._root.style.visibility = '';  //чтобы не было анимации
		this._checkIsAdaptive();
		if (this._contentDomNode && this._AutoFocusInput)
			this._contentDomNode.focus();
	}

	showWithoutBackground()
	{
		this.addClass('dialog--no-background dialog--no-shadow');
		this.show();
	}

	getDroppedDown()
	{
		return this._DroppedDown;
	}


	setPosition(left: number, top: number)
	{
		let container = this._root.querySelector('.dialog__container') as HTMLElement;
		container.style.position = 'absolute';
		container.style.left = left + 'px';
		container.style.top = top + 'px';
	}

	hide(closeFromBackgroundClick: boolean = false)
	{
		NP.removeOpenedDialog(this.getId());

		if (this.isLive())
		{
			this._DroppedDown = false;
			this.removeClass('dialog--open');
			this.EvClosed.fire(this, {CloseFromBackgroundClick: closeFromBackgroundClick});
			this._AutoDestroy && setTimeout(() => this.destroy(), 10);
			super.hide();
		}
	}

	setTabIndex(value: number)
	{
		super.setTabIndex(value);
		if (this._contentDomNode)
		{
			if (value != null)
				this._contentDomNode.tabIndex = value;
			else
				this._contentDomNode.removeAttribute("tabindex");
		}
	}

	setCloseButtonIsOuter(value: boolean)
	{
		if (!this._closeButton)
			this._closeButton = new IconToggleButton({
				FontImage: NP.FontImages.close_s,
				ToolTip: NP.RM.getString("Close"),
				EvClick: () => this._onCancelButtonClick()
			});

		if (value)
		{
			if (!this._outerButtonsNode)
			{
				this._outerButtonsNode = NP.createNode(this._container, 'dialog__outer-buttons');
				this._closeButton.putToNode(this._outerButtonsNode);
			}
		}
		else
		{
			if (this._outerButtonsNode)
			{
				this._container.removeChild(this._outerButtonsNode);
				this._outerButtonsNode = null;
			}
			this._closeButton.putToNode(this._topAppBar.getCaptionNode().parentNode as HTMLElement, this._topAppBar.getCaptionNode());
		}

		this._closeButton.show();
	}

	getTabIndex()
	{
		return (this._contentDomNode) ? this._contentDomNode.getAttribute("tabindex") : super.getTabIndex();
	}

	setFocus()
	{
		try
		{
			if (this._contentDomNode)
			{
				this.setTabIndex(0);
				setTimeout(() => this._contentDomNode.focus(), 0);
			}
		} catch (e)
		{
		}
	}

	destroy()
	{
		if (this._onResizeTimeout)
			clearTimeout(this._onResizeTimeout);

		NP.removeOpenedDialog(this.getId());

		super.destroy();
	}

	static getDetachedFrame()
	{
		return this._detachedFrame;
	}

	static showDetachedFrame()
	{
		if (!Dialog._detachedFrame)
			this._detachedFrame = NP.createNode(document.body, 'dialog__detached-frame');
	}

	/**
	 * когда устанавливается в true фон диалога не закрывается после NP.closeAllOpenedDialogs() либо при закрытии единственного диалога
	 * используется, чтобы не было визуального моргания фона диалога в сложных последовательностях открытия/закрытия диалогов.
	 * @param value
	 */
	static keepDetachedFrame(value: boolean)
	{
		this._keepDetachedFrame = value
	}

	static checkDetachedFrame()
	{
		setTimeout(() =>
		{
			if (Dialog._detachedFrame && !this._keepDetachedFrame && !NP.getOpenedDialogs().length)
			{
				Dialog._detachedFrame.remove();
				Dialog._detachedFrame = null;
			}
		}, 50);
	}
};

NP.Dialog = Dialog;


//в NP, потому что должно быть доступно в функциях NP (например NP.initHotKeys)
NP.OpenedDialogs = [];
NP.getOpenedDialogs = function ()
{
	return NP.OpenedDialogs;
};
NP.getLastOpenedDialog = function ()
{
	if (NP.OpenedDialogs.length)
		return NP.OpenedDialogs[NP.OpenedDialogs.length - 1];
	else
		return null;
};
NP.removeOpenedDialog = function (id: string)
{
	let index = NP.OpenedDialogs.findIndex((dialog: Dialog) => dialog.getId() == id);
	if (index > -1)
		NP.OpenedDialogs.splice(index, 1);
	Dialog.checkDetachedFrame();
};
NP.closeAllOpenedDialogs = function ()
{
	for (let i = NP.OpenedDialogs.length - 1; i >= 0; i--)
		NP.OpenedDialogs[i].destroy();
	NP.OpenedDialogs = [];
	Dialog.keepDetachedFrame(false);
	Dialog.checkDetachedFrame();
};


NP.showConfirm = function (settings: any)
{
	settings = settings || {};
	settings.Caption = settings.Caption || NP.RM.getString('Confirm');
	settings.ShowHeader = settings.ShowHeader != null ? settings.ShowHeader : true;
	settings.AutoDestroy = true;
	settings.Selectable = true;
	settings.IsMessageDialog = true;
	const dlg = new Dialog(settings);
	dlg.getTopAppBar().addClass('top-app-bar--no-shadow');
	dlg.addClass('confirm-dlg');
	dlg.getCancelButton().setIsVisible(!settings.ShowCloseButton);
	dlg.show();
	return dlg;
};

NP.showMessage = function (settings: any)
{
	if (NP.messageDialog)
		NP.messageDialog.hide();
	settings = settings || {};
	settings.Caption = settings.Caption || NP.RM.getString('Message');
	settings.ShowHeader = settings.ShowHeader != null ? settings.ShowHeader : true;
	settings.AutoDestroy = true;
	settings.Selectable = true;
	settings.IsMessageDialog = true;
	const dlg = NP.messageDialog = new Dialog(settings);
	dlg.getTopAppBar().addClass('top-app-bar--no-shadow');
	dlg.getOkButton().setIsVisible(false);
	dlg.getCancelButton().setRK(settings.CancelRK || 'Close');
	if (settings.CancelRK)
		dlg.getCancelButton().removeClass('app-button--neutral');
	dlg.show();
	return dlg;
};

NP.showCommonErrorMessage = function (message: string)
{
	let dlg = NP._cmdErrDlg = new Dialog({
		Caption: NP.RM.getString('Error'),
		ShowHeader: true,
		AutoDestroy: true,
		Selectable: true,
		IsMessageDialog: true,
		EvOkButtonClick: function ()
		{
			NP.showHelpWidget();
		},
		Content: NP.RM.getString('ErrorMessage'),
		EvClick: function (sender: Dialog, args: EventArgs)
		{
			if (NP.isCtrlKey(args.Event))
				sender.setContent(NP.escapeHTML(sender.getData('ErrorMessage')));
		},
		EvKeyDown: function (sender: Dialog, args: EventKeyArgs)
		{
			if (NP.isCtrlKey(args.Event) && args.Event.keyCode == NP.Key.I)
				sender.setContent(NP.escapeHTML(sender.getData('ErrorMessage')));
		}
	});
	dlg.setData(message, 'ErrorMessage');
	dlg.getOkButton().setIsVisible(false);
	dlg.getCancelButton().setRK('GotIt');
	dlg.getCancelButton().removeClass('app-button--neutral');
	dlg.show();
	return dlg;
};

NP.hideCommonErrorMessage = function ()
{
	if (NP._cmdErrDlg)
	{
		NP._cmdErrDlg.hide();
		NP._cmdErrDlg = null;
	}
	if (NP.messageDialog)
		NP.messageDialog.hide();
};

NP.checkOldBrowsers = function ()
{
	if (!NP.UnsupportedBrowserShowed && NP.IsIE)
	{
		NP.showMessage({
			RK: 'UnsupportedBrowser',
			EvCancelButtonClick: function ()
			{
				NP.UnsupportedBrowserShowed = true;
			}
		});
	}
};

